import { useMemo, useState } from 'react';

import type { UpcomingItem } from '../types.js';

const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

interface ReleasesTimelineProps {
  readonly entries: ReadonlyArray<UpcomingItem>;
}

interface TimelineNode {
  readonly dayKey: string; // YYYY-MM-DD pour regroupement
  readonly date: Date;
  readonly items: ReadonlyArray<UpcomingItem>;
  readonly pct: number;
  readonly above: boolean;
}

export function ReleasesTimeline({ entries }: ReleasesTimelineProps): JSX.Element {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const nodes = useMemo<ReadonlyArray<TimelineNode>>(() => {
    // Regroupe les entries par date (ISO YYYY-MM-DD)
    const groups = new Map<string, { date: Date; items: UpcomingItem[] }>();
    for (const e of entries) {
      if (!e.book.releaseDate) continue;
      const date = new Date(e.book.releaseDate);
      if (Number.isNaN(date.getTime())) continue;
      const key = date.toISOString().slice(0, 10);
      const existing = groups.get(key);
      if (existing) existing.items.push(e);
      else groups.set(key, { date, items: [e] });
    }
    if (groups.size === 0) return [];

    const sorted = Array.from(groups.entries()).sort(
      (a, b) => a[1].date.getTime() - b[1].date.getTime(),
    );
    const minTime = sorted[0]![1].date.getTime();
    const maxTime = sorted[sorted.length - 1]![1].date.getTime();
    const range = Math.max(1, maxTime - minTime);

    return sorted.map(([dayKey, group], idx) => ({
      dayKey,
      date: group.date,
      items: group.items,
      pct: ((group.date.getTime() - minTime) / range) * 100,
      // Alternance déterministe : pair en bas, impair en haut (zigzag)
      above: idx % 2 === 1,
    }));
  }, [entries]);

  if (nodes.length === 0) {
    return (
      <div className="timeline">
        <div className="timeline-track" style={{ minWidth: 'unset' }}>
          <p className="w-full text-center font-script text-base text-brown-soft">
            Aucune sortie à venir 🌿
          </p>
        </div>
      </div>
    );
  }

  // Si un seul point, on le centre — sinon il colle à gauche (pct=0).
  const onlyOne = nodes.length === 1;

  return (
    <div className="timeline">
      <div className="timeline-track">
        {nodes.map((n) => {
          const isOpen = openKey === n.dayKey;
          const left = onlyOne ? 50 : Math.min(95, Math.max(5, n.pct));
          const count = n.items.length;
          const headline = n.items[0]!.book.title;
          const dotSize = count >= 4 ? 22 : count >= 2 ? 18 : 14;
          return (
            <button
              key={n.dayKey}
              type="button"
              className={`timeline-event${n.above ? ' above' : ''}${isOpen ? ' open' : ''}`}
              style={{ left: `${left}%` }}
              onClick={() => setOpenKey(isOpen ? null : n.dayKey)}
              aria-expanded={isOpen}
              aria-label={
                count > 1
                  ? `${count} sorties le ${formatDate(n.date)}`
                  : `${headline} — ${formatDate(n.date)}`
              }
            >
              <span
                className="timeline-dot"
                aria-hidden
                style={{ width: dotSize, height: dotSize }}
              >
                {count > 1 && (
                  <span className="timeline-dot-count">{count}</span>
                )}
              </span>
              <span className="timeline-label" title={headline}>
                {count > 1 ? `${truncate(headline, 18)} +${count - 1}` : truncate(headline, 22)}
              </span>
              <span className="timeline-date">{formatDate(n.date)}</span>

              {isOpen && count > 1 && (
                <ul
                  className="timeline-popover"
                  onClick={(e) => e.stopPropagation()}
                >
                  {n.items.map((it) => (
                    <li key={it.id}>
                      <span className="font-serif font-semibold text-brown">
                        {it.book.title}
                      </span>
                      <span className="text-brown-soft"> — {it.book.author}</span>
                    </li>
                  ))}
                </ul>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatDate(d: Date): string {
  return `${d.getDate()} ${MONTH_FORMAT.format(d)}`;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1).trimEnd()}…`;
}
